// «Información general» de la ficha del cliente (Rodrigo, 2026-10-24): UNA sola ficha para crear y
// para mantener, como la «Mantención del Maestro» de Random. El cliente nuevo y el ya creado se ven
// igual: Identificación, Dirección principal y Contacto, en recuadros con título. Al crear se llena
// todo aquí; ya creado, la dirección y los contactos se administran en sus pestañas (aquí se ven).
import type { ReactNode } from 'react';
import { useComunas } from '../api/consultas';

export type TipoEntidad = 'cliente' | 'ambos' | 'proveedor';
export const NOMBRE_TIPO_ENTIDAD: Record<TipoEntidad, string> = {
  cliente: 'Cliente',
  ambos: 'Cliente y proveedor',
  proveedor: 'Proveedor',
};

export interface ValoresGenerales {
  rut: string;
  dv: string;
  razonSocial: string;
  nombreFantasia: string;
  giro: string;
  tipoEntidad: TipoEntidad;
  calle: string;
  numero: string;
  comuna: string;
  referencia: string;
  telefono: string;
  correo: string;
  correoDte: string;
}

/** Texto de ficha: siempre en mayúsculas (los correos, en minúscula). */
const may = (v: string) => v.toUpperCase();

export function InformacionGeneral({
  v,
  poner,
  alta,
  editable,
  estado,
  tiposEntidad = ['cliente', 'ambos'],
  alRut,
  alIrA,
}: {
  v: ValoresGenerales;
  poner: (c: Partial<ValoresGenerales>) => void;
  /** Cliente nuevo: el RUT, la dirección y el contacto se digitan aquí. */
  alta: boolean;
  /** Puede cambiar razón social, fantasía, giro y tipo de entidad. */
  editable: boolean;
  estado?: ReactNode;
  tiposEntidad?: TipoEntidad[];
  /** Al digitar el RUT (alta): calcula el dígito. */
  alRut?: (rut: string) => void;
  /** Ya creado: ir a la pestaña donde se administra la dirección o el contacto. */
  alIrA?: (seccion: 'direcciones' | 'contactos') => void;
}) {
  const comunas = useComunas();
  const nombreComuna = comunas.data?.find((c) => c.codigo === v.comuna)?.nombre ?? v.comuna;
  return (
    <div className="ficha-seccion informacion-general">
      <fieldset className="recuadro-ficha">
        <legend>Identificación</legend>
        <div className="ficha-grilla">
          <div className="campo">
            <span className="etiqueta">RUT</span>
            <span className="rut-dv">
              <input
                aria-label="RUT"
                inputMode="numeric"
                disabled={!alta}
                {...(alta ? { 'data-autofoco': true } : {})}
                value={v.rut}
                onChange={(e) => alRut?.(e.target.value.replace(/\D/g, ''))}
              />
              <span>-</span>
              <input
                aria-label="Dígito verificador"
                className="dv"
                maxLength={1}
                disabled={!alta}
                value={v.dv}
                onChange={(e) => poner({ dv: e.target.value.toUpperCase() })}
              />
            </span>
          </div>
          <div className="campo">
            <span className="etiqueta">Tipo de entidad</span>
            <select
              aria-label="Tipo de entidad"
              disabled={!editable}
              value={v.tipoEntidad}
              onChange={(e) => poner({ tipoEntidad: e.target.value as TipoEntidad })}
            >
              {tiposEntidad.map((t) => (
                <option key={t} value={t}>
                  {NOMBRE_TIPO_ENTIDAD[t]}
                </option>
              ))}
            </select>
          </div>
          {estado !== undefined && (
            <div className="campo">
              <span className="etiqueta">Estado</span>
              <span>{estado}</span>
            </div>
          )}
          <div className="campo ancho-2">
            <span className="etiqueta">Razón social o nombre</span>
            <input
              aria-label="Razón social"
              disabled={!editable}
              value={v.razonSocial}
              onChange={(e) => poner({ razonSocial: may(e.target.value) })}
            />
          </div>
          <div className="campo">
            <span className="etiqueta">Nombre de fantasía</span>
            <input
              aria-label="Nombre de fantasía"
              disabled={!editable}
              value={v.nombreFantasia}
              onChange={(e) => poner({ nombreFantasia: may(e.target.value) })}
            />
          </div>
          <div className="campo ancho-2">
            <span className="etiqueta">Giro (para la factura)</span>
            <input
              aria-label="Giro"
              disabled={!editable}
              value={v.giro}
              onChange={(e) => poner({ giro: may(e.target.value) })}
            />
          </div>
        </div>
      </fieldset>
      <fieldset className="recuadro-ficha">
        <legend>Dirección principal (tributaria y de despacho)</legend>
        <div className="ficha-grilla">
          <div className="campo ancho-2">
            <span className="etiqueta">Calle</span>
            <input
              aria-label="Calle"
              disabled={!alta}
              value={v.calle}
              onChange={(e) => poner({ calle: may(e.target.value) })}
            />
          </div>
          <div className="campo">
            <span className="etiqueta">N°</span>
            <input
              aria-label="Número"
              disabled={!alta}
              value={v.numero}
              onChange={(e) => poner({ numero: may(e.target.value) })}
            />
          </div>
          <div className="campo">
            <span className="etiqueta">Comuna</span>
            {alta ? (
              <select
                aria-label="Comuna"
                value={v.comuna}
                onChange={(e) => poner({ comuna: e.target.value })}
              >
                <option value="">Comuna…</option>
                {(comunas.data ?? []).map((c) => (
                  <option key={c.codigo} value={c.codigo}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            ) : (
              <input aria-label="Comuna" disabled value={nombreComuna} />
            )}
          </div>
          <div className="campo ancho-2">
            <span className="etiqueta">Referencia (opcional)</span>
            <input
              aria-label="Referencia"
              disabled={!alta}
              value={v.referencia}
              onChange={(e) => poner({ referencia: may(e.target.value) })}
            />
          </div>
        </div>
        {!alta && alIrA && (
          <button type="button" className="enlace" onClick={() => alIrA('direcciones')}>
            Administrar direcciones y sucursales →
          </button>
        )}
      </fieldset>
      <fieldset className="recuadro-ficha">
        <legend>Contacto{alta ? ' (obligatorio: hay que poder ubicar al cliente)' : ''}</legend>
        <div className="ficha-grilla">
          <div className="campo">
            <span className="etiqueta">Teléfono</span>
            <input
              aria-label="Teléfono"
              inputMode="tel"
              disabled={!alta}
              value={v.telefono}
              onChange={(e) => poner({ telefono: e.target.value })}
            />
          </div>
          <div className="campo">
            <span className="etiqueta">Correo</span>
            <input
              aria-label="Correo comercial"
              type="email"
              disabled={!alta}
              value={v.correo}
              onChange={(e) => poner({ correo: e.target.value.toLowerCase() })}
            />
          </div>
          <div className="campo">
            <span className="etiqueta">Correo factura electrónica (DTE)</span>
            <input
              aria-label="Correo DTE"
              type="email"
              disabled={!alta}
              placeholder={alta ? 'el mismo correo, si no es otro' : ''}
              value={v.correoDte}
              onChange={(e) => poner({ correoDte: e.target.value.toLowerCase() })}
            />
          </div>
        </div>
        {!alta && alIrA && (
          <button type="button" className="enlace" onClick={() => alIrA('contactos')}>
            Administrar contactos y correos →
          </button>
        )}
      </fieldset>
    </div>
  );
}
